import FileUploader from "@via/components/file-uploader/file-uploader";
import Modal from "@via/components/modal";
import {
  SpreadsheetData,
  SpreadsheetEditor,
} from "@via/components/spreadsheet-editor/spreadsheet-editor";
import { useCreateVmetaData } from "@via/pages/projects/metadata/metadata.api.tsx";
import {
  useDownloadDatasetTemplate,
  useGetProjectDatasetFields,
} from "@via/pages/data/premium-dataset/premium-dataset.api.tsx";
import { useUploadSpreadsheet } from "@via/pages/data/premium-dataset/premium-dataset.api.tsx";
import { ModalSize } from "@via/utils/constants";
import { useCallback, useState, useEffect } from "react";
import { useNotifications } from "@via/hooks/useNotifications";
import {
  BannerType,
  MessageBanner,
} from "@via/components/message-banner/message-banner";
import IndeterminateCheckbox from "@via/components/inputs/indeterminate-checkbox";
import {
  processError,
  formatSpreadsheetCellValue,
  getInitialColumnVisibility,
  applyDefaultHiddenColumns,
} from "@via/pages/data/premium-dataset/utils/helper";

interface AddCollectionFileProps {
  isOpen: boolean;
  canvasId: string;
  collectionName: string;
  fieldTypes?: Array<{ name?: string; label?: string }>;
  onClose: () => void;
}

const AddCollectionFile = ({
  isOpen,
  canvasId,
  collectionName,
  fieldTypes,
  onClose,
}: AddCollectionFileProps) => {
  const { setNotification } = useNotifications();
  const [columnLabels, setColumnLabels] = useState<string[]>([]);
  const [spreadsheetData, setSpreadsheetData] = useState<SpreadsheetData>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [changedRows, setChangedRows] = useState<Set<number>>(new Set());
  const [isExcelSheetUsed, setIsExcelSheetUsed] = useState(false);
  const [hasDataChanges, setHasDataChanges] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >({});

  const { mutateAsync: createVmetaData, isPending: isCreateVmetaDataLoading } =
    useCreateVmetaData();

  const {
    data: uploadedSpreadsheetData,
    mutateAsync: uploadSpreadsheet,
    isPending: isUploadSpreadsheetLoading,
    isSuccess: isUploadSpreadsheetSuccess,
  } = useUploadSpreadsheet();

  const {
    data: downloadCollectionTemplateData,
    mutateAsync: downloadCollectionTemplate,
    isPending: isDownloadTemplateLoading,
    isSuccess: isDownloadTemplateSuccess,
  } = useDownloadDatasetTemplate();

  const {
    data: collectionFields,
    mutateAsync: getCollectionFields,
    isSuccess: isGetCollectionFieldsSuccess,
  } = useGetProjectDatasetFields();

  const handleFileUpload = useCallback(
    (files: File[]) => {
      if (files.length === 0) return;
      if (isExcelSheetUsed) {
        uploadSpreadsheet(files[0]);
      }
    },
    [uploadSpreadsheet, isExcelSheetUsed]
  );

  useEffect(() => {
    if (isUploadSpreadsheetSuccess && uploadedSpreadsheetData) {
      const labels = uploadedSpreadsheetData?.data?.[0] || [];
      const rows = uploadedSpreadsheetData?.data?.slice(1) || [];
      const spreadsheetRows: { value: string }[][] = [];

      if (rows.length > 0) {
        // Map each row into the required format
        rows.forEach(row => {
          // check if row is full of null, don't add it
          if (row.every(cell => cell === null || cell === undefined)) {
            return;
          }
          const formattedRow = row.map(cell =>
            formatSpreadsheetCellValue(cell)
          );
          const newIndex = spreadsheetRows.length;
          spreadsheetRows.push(formattedRow);
          setChangedRows(prev => new Set(prev).add(newIndex));
          setHasDataChanges(true);
        });
      } else {
        // Create one empty row if no rows exist
        const emptyRow = labels.map(() => ({ value: "" }));
        spreadsheetRows.push(emptyRow);
        setChangedRows(prev => new Set(prev).add(0));
        setHasDataChanges(false);
      }

      setColumnLabels(labels as string[]);
      setSpreadsheetData(spreadsheetRows);
      setErrors([]);

      // Auto-hide columns with no values
      if (spreadsheetRows.length > 0) {
        const initialVisibility = getInitialColumnVisibility(
          labels as string[],
          spreadsheetRows
        );
        setColumnVisibility(
          collectionName === "file"
            ? applyDefaultHiddenColumns(initialVisibility, labels as string[])
            : initialVisibility
        );
      }
    }
  }, [isUploadSpreadsheetSuccess, uploadedSpreadsheetData]);

  useEffect(() => {
    if (isDownloadTemplateSuccess && downloadCollectionTemplateData) {
      try {
        const blob = new Blob([downloadCollectionTemplateData], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${collectionName}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } catch (error) {
        console.log("Error transforming data to blob:", error);
      }
    }
  }, [
    isDownloadTemplateSuccess,
    downloadCollectionTemplateData,
    collectionName,
  ]);

  // Fetch collection fields when modal opens
  useEffect(() => {
    if (isOpen && canvasId && collectionName) {
      getCollectionFields({
        canvasId,
        collectionName,
      });
    }
  }, [isOpen, canvasId, collectionName, getCollectionFields]);

  // Initialize spreadsheet editor with collection fields
  useEffect(() => {
    if (isGetCollectionFieldsSuccess && collectionFields) {
      const fields = collectionFields?.data?.[0] || [];
      const labels = fields.map((fieldName: string) => {
        const match = fieldTypes?.find(ft => ft?.name === fieldName);
        return (match?.label as string) || fieldName;
      });
      setColumnLabels(labels);

      // Initialize spreadsheetData with empty rows based on the number of fields
      const initialRows = Array.from({ length: 1 }, () =>
        labels.map(() => ({ value: "" }))
      );
      setSpreadsheetData(initialRows);

      // Show all columns by default when no file is uploaded
      const allVisible = labels.reduce(
        (acc, label) => {
          acc[label] = true;
          return acc;
        },
        {} as Record<string, boolean>
      );
      setColumnVisibility(
        collectionName === "file"
          ? applyDefaultHiddenColumns(allVisible, labels)
          : allVisible
      );
    }
  }, [isGetCollectionFieldsSuccess, collectionFields, fieldTypes]);

  const updateSpreadsheetData = useCallback(
    (newData: SpreadsheetData, rowIndex?: number, rowIndices?: number[]) => {
      setSpreadsheetData(newData);

      // Track changed rows
      const changedRowIndices =
        rowIndices && rowIndices.length > 0
          ? rowIndices
          : rowIndex !== undefined
            ? [rowIndex]
            : [];
      if (changedRowIndices.length > 0) {
        setChangedRows(prev => new Set([...prev, ...changedRowIndices]));
        setHasDataChanges(true);
      }
    },
    []
  );

  const handleConfirm = useCallback(async () => {
    const files: Record<string, unknown>[] = [];
    const internalErrors: string[] = [];
    setErrors([]);

    try {
      // Only process rows that have been modified
      const changedRowsArray = Array.from(changedRows);

      changedRowsArray.forEach(rowIndex => {
        const row = spreadsheetData[rowIndex];

        // Convert spreadsheetData to files array
        const file: Record<string, unknown> = {};
        row.forEach((cell, colIndex) => {
          const label = columnLabels[colIndex];
          // Only include visible columns
          if (label && columnVisibility[label] !== false) {
            const stringValue = cell.value?.toString() ?? "";
            const value = stringValue === "" ? null : stringValue;
            file[label] =
              value != null && (value.startsWith("{") || value.startsWith("["))
                ? JSON.parse(value)
                : value;
          }
        });
        files.push(file);
      });

      const promises = files.map((file, index) =>
        createVmetaData({
          canvasId,
          collectionName,
          file,
        }).catch(err => {
          return { error: err, index };
        })
      );

      // Execute all promises concurrently
      const results = await Promise.allSettled(promises);

      const errorRowIndices = new Set<number>();

      results.forEach((result, i) => {
        if (result.status === "rejected") {
          processError(result.reason, i, internalErrors);
          errorRowIndices.add(changedRowsArray[i]);
        } else if (result.status === "fulfilled" && "error" in result.value) {
          processError(result.value.error, i, internalErrors);
          errorRowIndices.add(changedRowsArray[i]);
        }
      });

      if (internalErrors.length) {
        setErrors(internalErrors);
        setChangedRows(errorRowIndices);
      } else {
        setChangedRows(new Set());
        onClose();
      }
    } catch (error: any) {
      setNotification({
        id: "error-transforming-data",
        message: "Error transforming data",
        details:
          error?.message || "Please ensure the data is in the correct format.",
        variant: "error",
      });
    }
  }, [
    changedRows,
    spreadsheetData,
    columnLabels,
    columnVisibility,
    createVmetaData,
    canvasId,
    collectionName,
    setNotification,
    onClose,
  ]);

  const handleClose = useCallback(() => {
    setSpreadsheetData([]);
    setColumnLabels([]);
    setErrors([]);
    setChangedRows(new Set());
    setIsExcelSheetUsed(false);
    setColumnVisibility({});
    onClose();
  }, [onClose]);

  const handleDownload = useCallback(() => {
    downloadCollectionTemplate({
      canvasId,
      collectionName,
    });
  }, [downloadCollectionTemplate, canvasId, collectionName]);

  return (
    <Modal
      open={isOpen}
      setOpen={handleClose}
      title="Add File to Collection"
      size={ModalSize.Large}
      width={1000}
      showButtons={false}>
      <div className="flex-2 min-w-80 my-4">
        <div className="flex items-baseline gap-2">
          <div className="mt-3 flex flex-row items-center">
            <label className="block pr-4 text-sm leading-6 text-gray-900">
              Upload spreadsheet
            </label>
            <IndeterminateCheckbox
              checked={isExcelSheetUsed}
              onChange={() => setIsExcelSheetUsed(!isExcelSheetUsed)}
              onClick={e => {
                e.stopPropagation();
              }}
            />
          </div>
        </div>
        {isExcelSheetUsed && (
          <div className="flex my justify-center">
            <button
              id="download-button"
              type="button"
              disabled={isDownloadTemplateLoading}
              onClick={e => {
                e.preventDefault();
                handleDownload();
              }}
              className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
              Download Template
            </button>
          </div>
        )}
      </div>
      {isExcelSheetUsed && (
        <div className="mb-4">
          <FileUploader
            onUpload={handleFileUpload}
            maxFiles={1}
            isLoading={false}
            progressPercentageList={[]}
            acceptedTypes={[".csv", ".tsv", ".xls", ".xlsx"]}
            containerClassName="w-full"
          />
        </div>
      )}

      <div className="my-4 max-h-[250px] overflow-auto">
        <MessageBanner messages={errors} type={BannerType.Error} />
      </div>

      {spreadsheetData.length > 0 && (
        <div className="mb-5">
          <SpreadsheetEditor
            columnLabels={columnLabels}
            data={spreadsheetData}
            onChange={updateSpreadsheetData}
            isLoading={isCreateVmetaDataLoading || isUploadSpreadsheetLoading}
            disableContextMenu
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={setColumnVisibility}
          />
        </div>
      )}

      <div className="flex justify-end mt-4">
        <button
          id="cancel-button"
          type="button"
          className="ml-4 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          onClick={handleClose}>
          Cancel
        </button>
        <button
          id="create-button"
          type="button"
          disabled={
            isCreateVmetaDataLoading ||
            isUploadSpreadsheetLoading ||
            !hasDataChanges
          }
          onClick={e => {
            e.preventDefault();
            handleConfirm();
          }}
          className="ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
          Create
        </button>
      </div>
    </Modal>
  );
};

export default AddCollectionFile;
